// src/provider.ts
import {
  ConfigurationError,
  AsyncSemaphore,
  InvalidInferenceRequestError,
  ModelCapabilityMismatchError,
  ModelNotFoundError,
  OperationCancelledError,
  ProviderAuthenticationError,
  ProviderOverloadedError,
  ProviderPermissionError,
  ProviderProtocolError,
  ProviderRateLimitError,
  ProviderTimeoutError,
  ProviderUnavailableError,
  MentisError
} from "@pi-mentis/pi-mentis-core";
import {
  assertSupportedEmbeddingDimension,
  getVerifiedEmbeddingModel,
  validateEmbeddingVector
} from "@pi-mentis/pi-mentis-inference";
function errorForStatus(status, context) {
  const details = { statusCode: status };
  const common = {
    operation: context.operation,
    provider: "openrouter",
    model: context.model,
    retryable: status === 429 || status >= 500,
    details
  };
  const message = `OpenRouter ${context.operation} failed with HTTP ${status}`;
  if (status === 400) return new InvalidInferenceRequestError(message, common);
  if (status === 401) return new ProviderAuthenticationError(message, common);
  if (status === 403 || status === 402) return new ProviderPermissionError(message, common);
  if (status === 404) return new ModelNotFoundError(message, common);
  if (status === 429) return new ProviderRateLimitError(message, common);
  if (status === 502 || status === 503) return new ProviderOverloadedError(message, common);
  return new ProviderUnavailableError(message, common);
}
async function delay(ms, signal) {
  if (signal?.aborted === true) throw new OperationCancelledError("OpenRouter retry cancelled");
  await new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new OperationCancelledError("OpenRouter retry cancelled"));
      },
      { once: true }
    );
  });
}
async function requestJson(url, apiKey, context, body) {
  const attempts = body === void 0 ? 1 : Math.max(1, context.maxAttempts ?? 1);
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt++) {
    const timeout = AbortSignal.timeout(context.timeoutMs);
    const signal = context.signal === void 0 ? timeout : AbortSignal.any([context.signal, timeout]);
    try {
      const response = await fetch(url, {
        method: body === void 0 ? "GET" : "POST",
        headers: {
          authorization: `Bearer ${apiKey}`,
          accept: "application/json",
          ...body === void 0 ? {} : { "content-type": "application/json" }
        },
        ...body === void 0 ? {} : { body: JSON.stringify(body) },
        signal
      });
      if (!response.ok) {
        await response.body?.cancel();
        throw errorForStatus(response.status, context);
      }
      const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
      if (!contentType.includes("application/json")) {
        await response.body?.cancel();
        throw new ProviderProtocolError("OpenRouter returned a non-JSON response", {
          operation: context.operation,
          provider: "openrouter",
          retryable: false
        });
      }
      return await response.json();
    } catch (error) {
      if (context.signal?.aborted === true) {
        throw new OperationCancelledError(`OpenRouter ${context.operation} request cancelled`, {
          operation: context.operation,
          provider: "openrouter",
          retryable: false,
          cause: error
        });
      }
      if (timeout.aborted) {
        lastError = new ProviderTimeoutError(
          `OpenRouter ${context.operation} exceeded ${context.timeoutMs}ms`,
          { operation: context.operation, provider: "openrouter", retryable: true, cause: error }
        );
      } else if (error instanceof MentisError) {
        lastError = error;
      } else {
        lastError = new ProviderUnavailableError(
          `OpenRouter ${context.operation} network request failed`,
          {
            operation: context.operation,
            provider: "openrouter",
            retryable: true,
            cause: error
          }
        );
      }
      const retryable = lastError instanceof MentisError && lastError.context.retryable === true;
      if (!retryable || attempt + 1 >= attempts) throw lastError;
      const base = context.baseDelayMs ?? 200;
      const cap = context.maxDelayMs ?? 3e3;
      await delay(Math.min(cap, base * 2 ** attempt), context.signal);
    }
  }
  throw lastError;
}
function object(value, label) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new ProviderProtocolError(`${label} must be an object`, {
      operation: "provider-response-validate",
      provider: "openrouter",
      retryable: false
    });
  }
  return value;
}
function embeddingVectors(value, count, dimensions) {
  const root = object(value, "Embedding response");
  if (root["object"] !== "list" || typeof root["model"] !== "string" || !Array.isArray(root["data"])) {
    throw new ProviderProtocolError(
      "OpenRouter embedding response requires object=list, model, and data",
      {
        operation: "provider-response-validate",
        provider: "openrouter",
        retryable: false
      }
    );
  }
  if (root["data"].length !== count) {
    throw new ProviderProtocolError(
      `OpenRouter returned ${root["data"].length} vectors for ${count} inputs`,
      {
        operation: "embedding-response-map",
        provider: "openrouter",
        retryable: false
      }
    );
  }
  const vectors = new Array(count);
  for (const raw of root["data"]) {
    const item = object(raw, "Embedding item");
    const index = item["index"];
    if (!Number.isSafeInteger(index) || index < 0 || index >= count || vectors[index] !== void 0) {
      throw new ProviderProtocolError("OpenRouter embedding response contains an invalid index", {
        operation: "embedding-response-map",
        provider: "openrouter",
        retryable: false
      });
    }
    const rawVector = item["embedding"];
    let values;
    if (typeof rawVector === "string") {
      const bytes = Buffer.from(rawVector, "base64");
      if (bytes.length !== dimensions * 4) {
        throw new ProviderProtocolError("OpenRouter base64 embedding has an invalid length", {
          operation: "embedding-response-map",
          provider: "openrouter",
          retryable: false
        });
      }
      const decoded = new Float32Array(dimensions);
      for (let offset = 0; offset < dimensions; offset++)
        decoded[offset] = bytes.readFloatLE(offset * 4);
      values = decoded;
    } else if (Array.isArray(rawVector)) {
      values = rawVector;
    } else {
      throw new ProviderProtocolError("OpenRouter embedding vector is invalid", {
        operation: "embedding-response-map",
        provider: "openrouter",
        retryable: false
      });
    }
    vectors[index] = validateEmbeddingVector(values, dimensions, false);
  }
  return vectors;
}
var OpenRouterEmbeddingProvider = class {
  id = "openrouter";
  #config;
  #apiKey;
  #capability;
  #semaphore;
  constructor(config, environment = process.env) {
    this.#config = config;
    const apiKey = environment[config.apiKeyEnv];
    if (apiKey === void 0 || apiKey.trim() === "") {
      throw new ProviderAuthenticationError(
        `OpenRouter credential environment variable ${config.apiKeyEnv} is not set`,
        { operation: "provider-initialize", provider: this.id, retryable: false }
      );
    }
    this.#apiKey = apiKey;
    this.#semaphore = new AsyncSemaphore(config.rateLimits.concurrentRequests);
    this.#capability = getVerifiedEmbeddingModel(config.embedding.model, "openrouter");
    assertSupportedEmbeddingDimension(this.#capability, config.embedding.dimensions);
  }
  capabilities() {
    return Promise.resolve({ models: [this.#capability] });
  }
  async embed(request, options = {}) {
    if (request.inputs.length === 0 || request.inputs.some((input) => input.length === 0)) {
      throw new ConfigurationError("Embedding inputs must be non-empty", {
        operation: "embedding",
        provider: this.id,
        model: this.#config.embedding.model,
        retryable: false
      });
    }
    assertSupportedEmbeddingDimension(this.#capability, request.dimensions);
    if (request.truncate !== void 0 && request.truncate !== "reject") {
      throw new ModelCapabilityMismatchError("OpenRouter embedding truncation is not supported", {
        operation: "embedding",
        provider: this.id,
        model: this.#config.embedding.model,
        retryable: false
      });
    }
    const release = await this.#semaphore.acquire(options.signal);
    let value;
    try {
      value = await requestJson(
        `${this.#config.baseUrl.replace(/\/$/u, "")}/embeddings`,
        this.#apiKey,
        {
          operation: "embedding",
          model: this.#config.embedding.model,
          timeoutMs: options.timeoutMs ?? this.#config.timeout.embeddingMs,
          maxAttempts: this.#config.retry.maxAttempts,
          baseDelayMs: this.#config.retry.baseDelayMs,
          maxDelayMs: this.#config.retry.maxDelayMs,
          ...options.signal === void 0 ? {} : { signal: options.signal }
        },
        {
          model: this.#config.embedding.model,
          input: request.inputs,
          encoding_format: this.#config.embedding.encodingFormat,
          ...this.#capability.supportsDimensionSelection ? { dimensions: request.dimensions } : {}
        }
      );
    } finally {
      release();
    }
    const usage = object(value, "Embedding response")["usage"];
    const usageRecord = typeof usage === "object" && usage !== null && !Array.isArray(usage) ? usage : void 0;
    const inputTokens = usageRecord?.["prompt_tokens"] ?? usageRecord?.["input_tokens"];
    const totalTokens = usageRecord?.["total_tokens"];
    return {
      model: this.#capability.model,
      vectors: embeddingVectors(value, request.inputs.length, request.dimensions),
      ...typeof inputTokens === "number" || typeof totalTokens === "number" ? {
        usage: {
          ...typeof inputTokens === "number" ? { inputTokens } : {},
          ...typeof totalTokens === "number" ? { totalTokens } : {}
        }
      } : {}
    };
  }
  async health(options = {}) {
    const started = performance.now();
    try {
      await this.embed(
        {
          inputs: ["health"],
          inputKind: "query",
          dimensions: this.#config.embedding.dimensions,
          truncate: "reject"
        },
        { ...options, timeoutMs: Math.min(options.timeoutMs ?? 5e3, 5e3) }
      );
      return { status: "healthy", checkedAt: Date.now(), latencyMs: performance.now() - started };
    } catch (error) {
      return {
        status: "unavailable",
        checkedAt: Date.now(),
        latencyMs: performance.now() - started,
        reason: error instanceof Error ? error.message : String(error)
      };
    }
  }
};
var OpenRouterConnectionTester = class {
  #provider;
  #dimensions;
  constructor(config, environment = process.env) {
    this.#provider = new OpenRouterEmbeddingProvider(config, environment);
    this.#dimensions = config.embedding.dimensions;
  }
  async testEmbedding() {
    await this.#provider.embed({
      inputs: ["health"],
      inputKind: "query",
      dimensions: this.#dimensions,
      truncate: "reject"
    });
  }
};
var OpenRouterModelCatalog = class {
  #config;
  #apiKey;
  constructor(config, environment = process.env) {
    this.#config = config;
    const apiKey = environment[config.apiKeyEnv];
    if (apiKey === void 0 || apiKey.trim() === "") {
      throw new ProviderAuthenticationError(
        `OpenRouter credential environment variable ${config.apiKeyEnv} is not set`,
        {
          operation: "models",
          provider: "openrouter",
          retryable: false
        }
      );
    }
    this.#apiKey = apiKey;
  }
  async list(options = {}) {
    const value = await requestJson(
      `${this.#config.baseUrl.replace(/\/$/u, "")}/embeddings/models`,
      this.#apiKey,
      {
        operation: "models",
        model: "catalog",
        timeoutMs: options.timeoutMs ?? 15e3,
        ...options.signal === void 0 ? {} : { signal: options.signal }
      }
    );
    const root = object(value, "Embedding model catalog");
    if (!Array.isArray(root["data"]))
      throw new ProviderProtocolError("OpenRouter model catalog requires data", {
        operation: "models",
        provider: "openrouter",
        retryable: false
      });
    return root["data"].map((raw) => object(raw, "Embedding model")["id"]).filter((id) => typeof id === "string");
  }
};
export {
  OpenRouterConnectionTester,
  OpenRouterEmbeddingProvider,
  OpenRouterModelCatalog
};
